import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

/**
 * Catch-all 404 page.
 *
 * Note: SPAs always serve index.html with HTTP 200 (that's how client-side
 * routing works). We add a `<meta name="robots" content="noindex">` so search
 * engines won't accidentally index junk URLs as soft-404s.
 */
export default function NotFoundPage() {
  const { t } = useTranslation();

  const meta = buildPageMeta("notFound");
  useSEO({
    robots: decideRobots({ pageType: "notFound" }),
    title: meta.title,
    description: meta.description,
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="not-found-page">
      <p className="text-6xl font-black text-[#5B8C66]">404</p>
      <h1 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight">
        {t("notfound.title", { defaultValue: "We couldn't find that page" })}
      </h1>
      <p className="mt-3 text-base text-[#5F6B61] max-w-md mx-auto">
        {t("notfound.body", {
          defaultValue:
            "The link may be broken, or the page may have moved. Try browsing all offers or pick a city to get started.",
        })}
      </p>
      <div className="mt-8 flex gap-3 justify-center flex-wrap">
        <Link to="/" className="btn-primary" data-testid="notfound-home">
          {t("notfound.home", { defaultValue: "Back to home" })}
        </Link>
        <Link to="/offers" className="btn-pill border border-[#1F2A24] px-5 py-3 text-sm font-semibold inline-flex items-center bg-white hover:bg-[#F5F1E6]" data-testid="notfound-offers">
          {t("notfound.browse", { defaultValue: "Browse all offers" })}
        </Link>
      </div>
    </div>
  );
}
